import api from './client';

// User Management Types
export interface UserListItem {
  id: string;
  username: string;
  coins: number;
  is_admin: boolean;
  grade: number | null;
  characters_count: number;
  created_at: string;
}

export interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface CharacterResponse {
  id: string;
  name: string;
  avatar_url: string | null;
  level: number;
  xp: number;
  created_at: string;
}

export interface UserDetailResponse {
  id: string;
  username: string;
  coins: number;
  is_admin: boolean;
  grade: number | null;
  created_at: string;
  characters: CharacterResponse[];
}

export interface UserUpdateRequest {
  coins?: number;
  is_admin?: boolean;
  grade?: number;
}

export interface CharacterUpdateRequest {
  xp?: number;
  level?: number;
}

// Character Type Types
export type CharacterTypeStatus = 'starter' | 'store' | 'disabled';

export interface CharacterType {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string;
  avatar_base: number;
  status: CharacterTypeStatus;
  price: number | null;
  enhancement_count: number | null;
}

export interface CharacterTypeCreateRequest {
  name: string;
  description?: string;
  avatar_url: string;
  avatar_base?: number;
  status?: CharacterTypeStatus;
  price?: number;
}

export interface CharacterTypeUpdateRequest {
  name?: string;
  description?: string;
  avatar_url?: string;
  avatar_base?: number;
  status?: CharacterTypeStatus;
  price?: number;
}

// Enhancement Type Types
export interface EnhancementType {
  id: string;
  name: string;
  display_name: string;
  sort_order: number;
}

export interface EnhancementTypeCreateRequest {
  name: string;
  display_name: string;
  sort_order?: number;
}

export interface EnhancementTypeUpdateRequest {
  name?: string;
  display_name?: string;
  sort_order?: number;
}

// Enhancement Types
export interface EnhancementOption {
  id: string;
  name: string;
  add_prompt: string;
  remove_prompt: string | null;
  preview_description: string | null;
}


// Category Types
export interface CategoryResponse {
  id: string;
  name: string;
  description: string | null;
  difficulty: number;  // 1=Easy, 2=Medium, 3=Hard
  skill_level: number;  // Global ordering (lower = earlier in progression)
  time_limit_seconds: number;
  cards_per_session: number;
  min_grade: number;
  max_grade: number;
  total_cards: number;
}

export interface CategoryCreateRequest {
  name: string;
  description?: string;
  difficulty?: number;  // 1=Easy, 2=Medium, 3=Hard
  skill_level?: number;  // Global ordering
  time_limit_seconds?: number;
  cards_per_session?: number;
  min_grade?: number;
  max_grade?: number;
}

export interface CategoryUpdateRequest {
  name?: string;
  description?: string;
  difficulty?: number;  // 1=Easy, 2=Medium, 3=Hard
  skill_level?: number;  // Global ordering
  time_limit_seconds?: number;
  cards_per_session?: number;
  min_grade?: number;
  max_grade?: number;
}

// User Management API
export const listUsers = async (page = 1, pageSize = 20, search?: string): Promise<UserListResponse> => {
  const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
  if (search) params.append('search', search);
  const res = await api.get(`/api/admin/users?${params}`);
  return res.data;
};

export const getUser = async (userId: string): Promise<UserDetailResponse> => {
  const res = await api.get(`/api/admin/users/${userId}`);
  return res.data;
};

export const updateUser = async (userId: string, data: UserUpdateRequest): Promise<UserDetailResponse> => {
  const res = await api.put(`/api/admin/users/${userId}`, data);
  return res.data;
};

export const getUserCharacters = async (userId: string): Promise<CharacterResponse[]> => {
  const res = await api.get(`/api/admin/users/${userId}/characters`);
  return res.data;
};

export const updateCharacter = async (
  userId: string,
  charId: string,
  data: CharacterUpdateRequest
): Promise<CharacterResponse> => {
  const res = await api.put(`/api/admin/users/${userId}/characters/${charId}`, data);
  return res.data;
};

export const deleteCharacter = async (userId: string, charId: string): Promise<void> => {
  await api.delete(`/api/admin/users/${userId}/characters/${charId}`);
};

// Character Type Management API
export const listCharacterTypes = async (): Promise<CharacterType[]> => {
  const res = await api.get('/api/admin/character-types');
  return res.data;
};

export const createCharacterType = async (data: CharacterTypeCreateRequest): Promise<CharacterType> => {
  const res = await api.post('/api/admin/character-types', data);
  return res.data;
};

export const updateCharacterType = async (
  typeId: string,
  data: CharacterTypeUpdateRequest
): Promise<CharacterType> => {
  const res = await api.put(`/api/admin/character-types/${typeId}`, data);
  return res.data;
};

export const deleteCharacterType = async (typeId: string): Promise<void> => {
  await api.delete(`/api/admin/character-types/${typeId}`);
};

// Enhancement Type API
export const listEnhancementTypes = async (): Promise<EnhancementType[]> => {
  const res = await api.get('/api/admin/enhancement-types');
  return res.data;
};

export const createEnhancementType = async (data: EnhancementTypeCreateRequest): Promise<EnhancementType> => {
  const res = await api.post('/api/admin/enhancement-types', data);
  return res.data;
};

export const updateEnhancementType = async (
  typeId: string,
  data: EnhancementTypeUpdateRequest
): Promise<EnhancementType> => {
  const res = await api.put(`/api/admin/enhancement-types/${typeId}`, data);
  return res.data;
};

export const deleteEnhancementType = async (typeId: string): Promise<void> => {
  await api.delete(`/api/admin/enhancement-types/${typeId}`);
};


// Category Management API
export const listCategories = async (): Promise<CategoryResponse[]> => {
  const res = await api.get('/api/admin/categories');
  return res.data;
};

export const createCategory = async (data: CategoryCreateRequest): Promise<CategoryResponse> => {
  const res = await api.post('/api/admin/categories', data);
  return res.data;
};

export const updateCategory = async (categoryId: string, data: CategoryUpdateRequest): Promise<CategoryResponse> => {
  const res = await api.put(`/api/admin/categories/${categoryId}`, data);
  return res.data;
};

export const deleteCategory = async (categoryId: string): Promise<void> => {
  await api.delete(`/api/admin/categories/${categoryId}`);
};

// Enhancement Tester Types
export interface TestGenerateRequest {
  base_image_url: string;
  prompt: string;
  option_id?: string;
}

export interface TestGenerateResponse {
  avatar_url: string;
  full_prompt: string;
}

// Enhancement Tester API
export const generateTestAvatar = async (data: TestGenerateRequest): Promise<TestGenerateResponse> => {
  const res = await api.post('/api/admin/enhancement-tester/generate', data);
  return res.data;
};

// Image Import Types
export interface ImportImageResponse {
  success: boolean;
  local_path?: string;
  error?: string;
}

// Image Import API
export const importImageFromUrl = async (url: string): Promise<ImportImageResponse> => {
  const res = await api.post('/api/admin/import-image', { url });
  return res.data;
};

// Character Type Enhancement Types
export interface CharacterTypeEnhancement {
  id: string;
  level: number;
  type_id: string;
  type_name: string;
  name: string;
  add_prompt: string;
  preview_description: string | null;
}

export interface CharacterTypeEnhancementCreateRequest {
  level: number;
  type_id: string;
  name: string;
  add_prompt: string;
  preview_description?: string;
}

export interface CharacterTypeEnhancementUpdateRequest {
  level?: number;
  type_id?: string;
  name?: string;
  add_prompt?: string;
  preview_description?: string;
}

// Character Type Enhancement API
export const getCharacterTypeEnhancements = async (typeId: string): Promise<CharacterTypeEnhancement[]> => {
  const res = await api.get(`/api/admin/character-types/${typeId}/enhancements`);
  return res.data;
};

export const createCharacterTypeEnhancement = async (
  typeId: string,
  data: CharacterTypeEnhancementCreateRequest
): Promise<CharacterTypeEnhancement> => {
  const res = await api.post(`/api/admin/character-types/${typeId}/enhancements`, data);
  return res.data;
};

export const updateCharacterTypeEnhancement = async (
  optionId: string,
  data: CharacterTypeEnhancementUpdateRequest
): Promise<CharacterTypeEnhancement> => {
  const res = await api.put(`/api/admin/enhancements/${optionId}`, data);
  return res.data;
};

export const deleteCharacterTypeEnhancement = async (optionId: string): Promise<void> => {
  await api.delete(`/api/admin/enhancements/${optionId}`);
};

// Team Image Types
export interface TeamImageStartResponse {
  job_id: string;
  status: string;
  player_id: string;
  character_count: number;
}

// Regenerate Avatar Types (Async)
export interface RegenerateAvatarStartResponse {
  job_id: string;
  status: string;
  character_id: string;
  character_name: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: string;
  job_type: string;
  character_id: string;
  created_at: string;
  updated_at: string;
  result?: {
    new_avatar_url: string;
    character_name: string;
    steps_count: number;
    steps: Array<{
      level: number;
      option_name: string;
      prompt: string;
      avatar_url: string;
    }>;
  };
  error?: string;
}

// Regenerate Avatar API (Async)
export const startRegeneration = async (characterId: string): Promise<RegenerateAvatarStartResponse> => {
  const res = await api.post(`/api/admin/characters/${characterId}/regenerate-avatar`);
  return res.data;
};

export const getJobStatus = async (jobId: string): Promise<JobStatusResponse> => {
  const res = await api.get(`/api/admin/jobs/${jobId}`);
  return res.data;
};

// Team Image API
export const startTeamImageGeneration = async (playerId: string): Promise<TeamImageStartResponse> => {
  const res = await api.post(`/api/admin/players/${playerId}/team-image`);
  return res.data;
};
